/**
 * StatusScreen — shared full-screen interruption card (License Not Found,
 * Session Expired, Permissions Error, Setup Required) so every "you can't
 * get in yet" screen shares the same icon-circle + eyebrow + title chrome,
 * instead of each page hand-rolling its own card styles.
 */

import type { ReactNode } from 'react';

interface StatusScreenProps {
  iconBg: string;
  iconColor: string;
  icon: ReactNode;
  title: string;
  subtitle: ReactNode;
  maxWidth?: number;
  children?: ReactNode;
  actions?: ReactNode;
}

export default function StatusScreen({
  iconBg,
  iconColor,
  icon,
  title,
  subtitle,
  maxWidth = 480,
  children,
  actions,
}: StatusScreenProps) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        padding: '24px 16px',
      }}
    >
      <div
        style={{
          maxWidth,
          width: '100%',
          background: '#ffffff',
          borderRadius: 16,
          boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
          padding: '36px 32px',
          border: '1px solid #e2e8f0',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 4 }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: iconBg,
              color: iconColor,
              marginBottom: 14,
            }}
          >
            {icon}
          </div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.15em',
              color: '#94a3b8',
              textTransform: 'uppercase',
              marginBottom: 10,
            }}
          >
            Content Trust Dashboard
          </div>
          <h1
            style={{
              margin: 0,
              fontSize: 22,
              fontWeight: 800,
              color: '#0f172a',
            }}
          >
            {title}
          </h1>
          <p
            style={{
              marginTop: 10,
              fontSize: 14,
              color: '#64748b',
              lineHeight: 1.6,
            }}
          >
            {subtitle}
          </p>
        </div>

        {children}

        {actions && (
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}

export function StatusPrimaryButton({
  children,
  onClick,
  full,
}: {
  children: ReactNode;
  onClick: () => void;
  full?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: full ? undefined : 1,
        width: full ? '100%' : undefined,
        padding: '11px 0',
        borderRadius: 10,
        border: 'none',
        background: '#6366f1',
        color: '#ffffff',
        fontSize: 14,
        fontWeight: 700,
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

export function StatusSecondaryButton({
  children,
  onClick,
  full,
}: {
  children: ReactNode;
  onClick: () => void;
  full?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: full ? undefined : 1,
        width: full ? '100%' : undefined,
        padding: '11px 0',
        borderRadius: 10,
        border: '1px solid #e2e8f0',
        background: '#ffffff',
        color: '#374151',
        fontSize: 14,
        fontWeight: 600,
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

export function StatusInfoBox({
  tone = 'gray',
  title,
  children,
}: {
  tone?: 'gray' | 'blue';
  title: string;
  children: ReactNode;
}) {
  const isBlue = tone === 'blue';
  return (
    <div
      style={{
        marginTop: 16,
        padding: '14px 16px',
        borderRadius: 10,
        background: isBlue ? '#eff6ff' : '#f8fafc',
        border: `1px solid ${isBlue ? '#bfdbfe' : '#e2e8f0'}`,
        fontSize: 13,
        color: isBlue ? '#1e40af' : '#475569',
        lineHeight: 1.6,
        textAlign: 'left',
      }}
    >
      <p
        style={{
          margin: '0 0 8px',
          fontWeight: 700,
          color: isBlue ? '#1e3a8a' : '#334155',
        }}
      >
        {title}
      </p>
      {children}
    </div>
  );
}
